import { zoneCrossingListQuerySchema, type Page, type ZoneCrossing, type ZoneWatchView } from '@chukua/common';
import { useEffect, useState } from 'react';
import { getZoneWatch, listZoneCrossings } from '../../../zones.api';

const PAGE_SIZE = 10;

export type ZoneWatchState =
  { status: 'loading' } | { status: 'ready'; watch: ZoneWatchView; crossings: Page<ZoneCrossing>; isPaging: boolean } | { status: 'error' };

interface Loaded {
  watch: ZoneWatchView;
  crossings: Page<ZoneCrossing>;
  page: number;
}

/** A watched zone's status, failed pushes and one page of arrivals and departures. Mount with `key={zoneId}`. Old rows stay up while the next page loads. */
export function useZoneWatch(zoneId: string) {
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const query = zoneCrossingListQuerySchema.parse({ page, pageSize: PAGE_SIZE });
    Promise.all([getZoneWatch(zoneId), listZoneCrossings(zoneId, query)])
      .then(([watch, crossings]) => {
        if (cancelled) return;
        setFailed(false);
        setLoaded({ watch, crossings, page });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [zoneId, page]);

  return { state: stateOf({ failed, loaded, page }), page, setPage, pageSize: PAGE_SIZE };
}

function stateOf({ failed, loaded, page }: { failed: boolean; loaded: Loaded | null; page: number }): ZoneWatchState {
  if (failed) return { status: 'error' };
  if (!loaded) return { status: 'loading' };
  return { status: 'ready', watch: loaded.watch, crossings: loaded.crossings, isPaging: loaded.page !== page };
}
